/**
 * Column preference utilities — allowlists, required-column enforcement,
 * ordering, and default restoration.
 *
 * Rules (from the frozen contract):
 *  1. Intersect the stored column list with the policy's `allowed` set.
 *  2. Add every `required` column if missing.
 *  3. Preserve the order of `allowed` (not the stored order) so columns are
 *     deterministic regardless of how they were saved.
 *  4. Fall back to `defaults` + `required` only when the intersection is empty.
 *
 * This module contains NO inspection-specific field names.
 */
// ---------------------------------------------------------------------------
// Core sanitization
// ---------------------------------------------------------------------------
/**
 * Sanitizes a raw column key list against the page's column policy.
 *
 * @param stored   Column keys retrieved from preferences (may be stale).
 * @param policy   Allowlist, required columns, and defaults for the page/context.
 * @returns        A clean, deterministically ordered column key list.
 */
export function sanitizeColumns(stored, policy) {
    // Intersect stored with allowed, preserving allowed order.
    const valid = policy.allowed.filter((key) => stored.includes(key));
    // If nothing survived the intersection, fall back to defaults.
    const base = valid.length > 0 ? valid : [...policy.defaults];
    // Ensure all required columns are present.
    const withRequired = policy.required.reduce((acc, key) => (acc.includes(key) ? acc : [...acc, key]), base);
    // Final pass: re-order by allowed order, keeping only the keys that survived.
    const finalSet = new Set(withRequired);
    return policy.allowed.filter((key) => finalSet.has(key));
}
/**
 * Returns the default visible column set for a policy, with required columns
 * always included.
 */
export function defaultColumns(policy) {
    return sanitizeColumns(policy.defaults, policy);
}
// ---------------------------------------------------------------------------
// Legacy hidden-columns conversion (Inspection Series)
// ---------------------------------------------------------------------------
/**
 * Converts a legacy `hiddenColumns` array into a visible column list.
 * Columns not in `hiddenColumns` and present in `policy.allowed` become
 * the visible set.
 *
 * @param hiddenColumns  Keys of columns that were hidden.
 * @param policy         The current page policy.
 */
export function visibleFromHidden(hiddenColumns, policy) {
    const hiddenSet = new Set(hiddenColumns);
    const visibleSet = new Set(policy.allowed.filter((key) => !hiddenSet.has(key)));
    policy.required.forEach((key) => visibleSet.add(key));
    return policy.allowed.filter((key) => visibleSet.has(key));
}
